'use client';

import { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Store, ArrowLeft, Send, CheckCircle, AlertCircle, Building2, Mail } from 'lucide-react';
import { Button, Checkbox, TextInput, Textarea } from '@mantine/core';
import DirectorySubmissionPublicService from '@/services/DirectorySubmissionPublicService';

interface AddBusinessClientProps {
  defaultCategory?: string;
  defaultCity?: string;
  defaultState?: string;
}

export default function AddBusinessClient({
  defaultCategory = '',
  defaultCity = '',
  defaultState = '',
}: AddBusinessClientProps) {
  const searchParams = useSearchParams();
  const [form, setForm] = useState({
    businessName: '',
    address: '',
    city: defaultCity,
    state: defaultState,
    zipCode: '',
    phone: '',
    website: '',
    primaryCategory: defaultCategory,
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    contactConsent: false,
    submitterComment: '',
    sourcePage: '',
    honeyPot: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'pending' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [existing, setExisting] = useState<{ slug: string | null; businessName: string; city: string | null; state: string | null } | null>(null);

  useEffect(() => {
    const source = searchParams.get('source') || (typeof window !== 'undefined' ? window.location.pathname : '');
    setForm((prev) => ({ ...prev, sourcePage: source }));
  }, [searchParams]);

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.businessName.trim() || !form.address.trim() || !form.city.trim() || !form.state.trim() || !form.ownerName.trim() || !form.ownerEmail.trim()) {
      setError('Please fill in the required fields.');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setError(null);
    setExisting(null);

    const result = await DirectorySubmissionPublicService.submitBusiness(form);

    if (result.success && result.pending) {
      setStatus('pending');
      setPendingEmail(result.email || form.ownerEmail);
      return;
    }

    if (result.success) {
      setStatus('success');
      setForm({
        businessName: '',
        address: '',
        city: defaultCity,
        state: defaultState,
        zipCode: '',
        phone: '',
        website: '',
        primaryCategory: defaultCategory,
        ownerName: '',
        ownerEmail: '',
        ownerPhone: '',
        contactConsent: false,
        submitterComment: '',
        sourcePage: typeof window !== 'undefined' ? window.location.pathname : '',
        honeyPot: '',
      });
      return;
    }

    if (result.error === 'already_listed' && result.existing) {
      setExisting({
        slug: result.existing.slug,
        businessName: result.existing.businessName,
        city: result.existing.city,
        state: result.existing.state,
      });
    }

    setError(
      result.error === 'already_listed'
        ? 'Looks like this one is already listed.'
        : result.error === 'invalid_input'
        ? 'Please check the form and try again.'
        : 'Something went wrong. Please try again.'
    );
    setStatus('error');
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Link
          href="/directory"
          className="inline-flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-blue-600 dark:hover:text-blue-400 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Directory
        </Link>

        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 md:p-8">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-7 h-7 text-blue-600" />
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
              Add Your Business
            </h1>
          </div>
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            List your business in the directory. Our team will review your submission before it goes live.
          </p>

          {status === 'success' ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
                Submission received
              </h2>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                We&apos;ll review your business details and publish the listing once verified.
              </p>
              <Button component={Link} href="/directory" variant="light">
                Back to Directory
              </Button>
            </div>
          ) : status === 'pending' ? (
            <div className="text-center py-8">
              <Mail className="w-12 h-12 text-blue-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
                Check your email
              </h2>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6 max-w-md mx-auto">
                We sent a confirmation link to <strong>{pendingEmail}</strong>. Click it to submit your business for review.
              </p>
              <p className="text-sm text-neutral-500 mb-6">
                Didn&apos;t receive it? Check your spam folder or try again in a few minutes.
              </p>
              <Button component={Link} href="/directory" variant="light">
                Back to Directory
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-800 dark:text-red-200 text-sm font-medium">{error}</p>
                    {existing && existing.slug && (
                      <Link
                        href={`/place/${existing.slug}`}
                        className="text-sm text-blue-600 dark:text-blue-400 underline mt-1 inline-block"
                      >
                        View {existing.businessName}
                      </Link>
                    )}
                  </div>
                </div>
              )}

              <TextInput
                label="Business name"
                placeholder="e.g. Patel’s African Grocery"
                value={form.businessName}
                onChange={(e) => handleChange('businessName', e.target.value)}
                required
              />

              <TextInput
                label="Address"
                placeholder="123 Market St"
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
                required
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextInput
                  label="City"
                  placeholder="Indianapolis"
                  value={form.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  required
                />
                <TextInput
                  label="State"
                  placeholder="IN"
                  value={form.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextInput
                  label="ZIP code"
                  placeholder="46204"
                  value={form.zipCode}
                  onChange={(e) => handleChange('zipCode', e.target.value)}
                />
                <TextInput
                  label="Phone"
                  placeholder="(317) 555-0100"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                />
              </div>

              <TextInput
                label="Category"
                placeholder="e.g. African grocery"
                value={form.primaryCategory}
                onChange={(e) => handleChange('primaryCategory', e.target.value)}
              />

              <TextInput
                label="Website (optional)"
                placeholder="https://example.com"
                type="url"
                value={form.website}
                onChange={(e) => handleChange('website', e.target.value)}
              />

              <div className="border-t border-neutral-200 dark:border-neutral-700 pt-5">
                <h3 className="text-sm font-medium text-neutral-900 dark:text-white mb-4">Owner Contact</h3>
                <div className="space-y-4">
                  <TextInput
                    label="Your name"
                    placeholder="Jane Doe"
                    value={form.ownerName}
                    onChange={(e) => handleChange('ownerName', e.target.value)}
                    required
                  />
                  <TextInput
                    label="Your email"
                    placeholder="jane@example.com"
                    type="email"
                    value={form.ownerEmail}
                    onChange={(e) => handleChange('ownerEmail', e.target.value)}
                    required
                  />
                  <TextInput
                    label="Your phone (optional)"
                    placeholder="(317) 555-0100"
                    value={form.ownerPhone}
                    onChange={(e) => handleChange('ownerPhone', e.target.value)}
                  />
                  <Checkbox
                    label="You may contact me about this listing"
                    description="Unchecked means we'll review the listing but won't reach out to you."
                    checked={form.contactConsent}
                    onChange={(e) => {
                      const checked = e.currentTarget.checked;
                      setForm((prev) => ({ ...prev, contactConsent: checked }));
                    }}
                  />
                </div>
              </div>

              <Textarea
                label="Additional information (optional)"
                placeholder="Hours, specialties, social links, or anything that helps verify the business..."
                value={form.submitterComment}
                onChange={(e) => handleChange('submitterComment', e.target.value)}
                rows={3}
              />

              {/* Honeypot — hidden from real users */}
              <div className="hidden" aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  value={form.honeyPot}
                  onChange={(e) => handleChange('honeyPot', e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <Button
                type="submit"
                variant='gradient' style={{ color: 'white' }}
                leftSection={<Store className="w-4 h-4" />}
                loading={status === 'submitting'}
                fullWidth
              >
                Submit Business for Review
              </Button>

              <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center">
                Submissions are reviewed before being published. If you just want to tip us off about another business,{' '}
                <Link href="/directory/suggest" className="underline text-blue-600 dark:text-blue-400">
                  suggest a business
                </Link>
                {' '}instead.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
